import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../components/FormField.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type {
  PlatformProviderModelDefinition,
  PlatformProviderModelRateInput,
  PlatformProviderModelTestResult,
  Tenant,
  ToastMessage,
} from '../../types.js';
import {
  manualProviderActiveDeployment,
  manualProviderRateFormValues,
  manualProviderRateState,
  type ManualProviderRateFormValues,
} from './manual-provider-rate-form.js';

type RateEditor = ManualProviderRateFormValues;

function rateEditorFor(model: PlatformProviderModelDefinition, tenantId: string): RateEditor {
  return manualProviderRateFormValues(manualProviderActiveDeployment(model.deployments, tenantId));
}

export function ModelDeploymentPanel({
  http,
  tenants,
  tenantId,
  selected,
  busy,
  setBusy,
  onToast,
  onDefinitionChanged,
  onOpenDefinitions,
  onOpenAssignment,
  onChanged,
}: {
  http: AdminHttpClient;
  tenants: Tenant[];
  tenantId: string;
  selected?: PlatformProviderModelDefinition;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  onDefinitionChanged: (model: PlatformProviderModelDefinition) => void;
  onOpenDefinitions: () => void;
  onOpenAssignment: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const translateError = useCallback(
    (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {})),
    [t]
  );
  const [targetTenants, setTargetTenants] = useState<string[]>([]);
  const [probe, setProbe] = useState<PlatformProviderModelTestResult>();
  const [rateEditor, setRateEditor] = useState<RateEditor>({
    inputRate: '',
    outputRate: '',
    cachedInputRate: '',
    cacheWriteInputRate: '',
    marginMultiplier: '1',
  });

  useEffect(() => {
    setTargetTenants(tenantId ? [tenantId] : []);
    setProbe(undefined);
    setRateEditor(
      selected
        ? rateEditorFor(selected, tenantId)
        : { inputRate: '', outputRate: '', cachedInputRate: '', cacheWriteInputRate: '', marginMultiplier: '1' }
    );
  }, [selected, tenantId]);

  const activeDeployments = useMemo(
    () => selected?.deployments?.filter((deployment) => deployment.status === 'active') ?? [],
    [selected]
  );
  const rateState = manualProviderRateState(rateEditor);

  function rateInput(): PlatformProviderModelRateInput | undefined {
    if (!selected || rateState.kind === 'invalid') return undefined;
    const verifiedAt = new Date(selected.pricingEvidence.verifiedAt);
    if (!selected.pricingEvidence.sourceUrl.trim() || Number.isNaN(verifiedAt.getTime()))
      return undefined;
    return {
      ...rateState.rate,
      sourceUrl: selected.pricingEvidence.sourceUrl,
      verifiedAt: verifiedAt.toISOString(),
    };
  }

  async function reloadSelected() {
    if (!selected) return;
    let offset = 0;
    let hasMore = true;
    while (hasMore) {
      const response = await http.listPlatformProviderModels({
        provider: selected.provider,
        limit: 100,
        offset,
      });
      const next = response.items.find((item) => item.modelKey === selected.modelKey);
      if (next) {
        onDefinitionChanged(next);
        return;
      }
      hasMore = offset + response.items.length < response.total && response.items.length > 0;
      offset += response.items.length;
    }
  }

  async function testModel() {
    if (!selected || !tenantId) return;
    setBusy(true);
    try {
      const result = await http.testPlatformProviderModel(
        selected.provider,
        selected.modelKey,
        tenantId
      );
      setProbe(result);
      onToast({
        tone: result.ok ? 'success' : 'error',
        message: result.ok
          ? t('manualProviderModels.probeOk', { latency: result.latencyMs })
          : t('manualProviderModels.probeFailed', { code: result.errorType ?? 'unknown' }),
      });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  async function deploy() {
    const rate = rateInput();
    if (!selected || !probe?.ok || !rate || !targetTenants.length) return;
    if (!window.confirm(t('manualProviderModels.deployConfirm', { count: targetTenants.length })))
      return;
    setBusy(true);
    try {
      const result = await http.deployPlatformProviderModel(selected.provider, selected.modelKey, {
        tenantIds: targetTenants,
        expectedRevision: selected.revision,
        rate,
      });
      onDefinitionChanged(result.definition);
      await onChanged();
      onToast({
        tone: 'success',
        message: !result.rateVersion
          ? `${t('manualProviderModels.deployed')} ${t('manualProviderModels.freeNotice')}`
          : t('manualProviderModels.deployed'),
      });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  async function publishRates() {
    const rate = rateInput();
    if (!selected || !rate || !targetTenants.length) return;
    setBusy(true);
    try {
      const result = await http.publishPlatformProviderModelRates(
        selected.provider,
        selected.modelKey,
        {
          tenantIds: targetTenants,
          expectedRevision: selected.revision,
          rate,
        }
      );
      await Promise.all([reloadSelected(), onChanged()]);
      onToast({
        tone: 'success',
        message: !result.rateVersion
          ? t('manualProviderModels.freeRatePublished')
          : t('manualProviderModels.ratePublished'),
      });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  async function revoke(tenant: string) {
    if (!selected || !window.confirm(t('manualProviderModels.revokeConfirm', { tenant }))) return;
    setBusy(true);
    try {
      await http.revokePlatformProviderModelDeployment(
        selected.provider,
        selected.modelKey,
        tenant
      );
      await Promise.all([reloadSelected(), onChanged()]);
      onToast({ tone: 'success', message: t('manualProviderModels.revoked') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, translateError) });
    } finally {
      setBusy(false);
    }
  }

  const rateValid = Boolean(rateInput());

  if (!selected) {
    return (
      <section className="panel form-grid">
        <h2>{t('platform.modelDeployment')}</h2>
        <p>{t('manualProviderModels.noDeploymentSelection')}</p>
        <button type="button" onClick={onOpenDefinitions}>
          {t('manualProviderModels.openDefinitions')}
        </button>
      </section>
    );
  }

  return (
    <section className="panel form-grid manual-model-lifecycle">
      <div className="section-heading">
        <div>
          <h2>{t('platform.modelDeployment')}</h2>
          <p className="screen-help">
            {selected.displayName} · {selected.provider} · {selected.modelKey}
          </p>
        </div>
        <button type="button" className="secondary" onClick={onOpenDefinitions}>
          {t('manualProviderModels.openDefinitions')}
        </button>
      </div>
      <p>{t('manualProviderModels.assignmentNotice')}</p>
      <p>
        <strong>{t('tenantScope.deploymentTarget')}</strong>
      </p>
      <div
        className="tenant-checks"
        role="group"
        aria-label={t('tenantScope.deploymentTarget')}
      >
        {tenants.map((tenant) => (
          <label className="check-row" key={tenant.id}>
            <input
              type="checkbox"
              checked={targetTenants.includes(tenant.id)}
              disabled={!tenantId}
              onChange={(event) =>
                setTargetTenants((current) =>
                  event.target.checked
                    ? [...new Set([...current, tenant.id])]
                    : current.filter((id) => id !== tenant.id)
                )
              }
            />
            <span>
              {tenant.name} ({tenant.id})
            </span>
          </label>
        ))}
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="secondary"
          disabled={busy || !tenantId || selected.status === 'disabled'}
          onClick={() => void testModel()}
        >
          {t('manualProviderModels.test')}
        </button>
        {probe ? (
          <span className={`badge ${probe.ok ? 'active' : ''}`}>
            {probe.ok
              ? t('manualProviderModels.probePassed')
              : t('manualProviderModels.probeFailedShort', {
                  code: probe.errorType ?? 'unknown',
                })}
          </span>
        ) : null}
      </div>
      <div className="manual-model-rates">
        <FormField
          label={t('manualProviderModels.inputRate')}
          type="number"
          min={0}
          step="any"
          value={rateEditor.inputRate}
          onChange={(event) =>
            setRateEditor((current) => ({ ...current, inputRate: event.target.value }))
          }
        />
        <FormField
          label={t('manualProviderModels.outputRate')}
          type="number"
          min={0}
          step="any"
          value={rateEditor.outputRate}
          onChange={(event) =>
            setRateEditor((current) => ({ ...current, outputRate: event.target.value }))
          }
        />
        <FormField
          label={t('manualProviderModels.cachedInputRate')}
          type="number"
          min={0}
          step="any"
          disabled={rateState.kind === 'free'}
          value={rateEditor.cachedInputRate}
          onChange={(event) =>
            setRateEditor((current) => ({ ...current, cachedInputRate: event.target.value }))
          }
        />
        <FormField
          label={t('manualProviderModels.cacheWriteInputRate')}
          type="number"
          min={0}
          step="any"
          disabled={rateState.kind === 'free'}
          value={rateEditor.cacheWriteInputRate}
          onChange={(event) =>
            setRateEditor((current) => ({ ...current, cacheWriteInputRate: event.target.value }))
          }
        />
        <FormField
          label={t('manualProviderModels.margin')}
          type="number"
          min="0.000000000001"
          step="any"
          disabled={rateState.kind === 'free'}
          title={
            rateState.kind === 'free' ? t('manualProviderModels.freeControlsDisabled') : undefined
          }
          value={rateEditor.marginMultiplier}
          onChange={(event) =>
            setRateEditor((current) => ({ ...current, marginMultiplier: event.target.value }))
          }
        />
      </div>
      <p className="field-help">{t('manualProviderModels.rateTransientHelp')}</p>
      <p className="field-help">{t('manualProviderModels.cacheRateHelp')}</p>
      <p className="field-help">{t('manualProviderModels.pricingEvidenceHelp')}</p>
      {rateState.kind === 'free' ? (
        <small className="field-help">{t('manualProviderModels.freeControlsDisabled')}</small>
      ) : null}
      {rateState.kind === 'invalid' && rateState.error === 'mixed_zero' ? (
        <small className="field-error">{t('manualProviderModels.mixedZeroRate')}</small>
      ) : rateState.kind === 'invalid' && rateState.error === 'free_cache_rate' ? (
        <small className="field-error">{t('manualProviderModels.freeCacheRate')}</small>
      ) : rateState.kind === 'invalid' && rateState.error === 'cached_input_above_input' ? (
        <small className="field-error">{t('manualProviderModels.cachedInputAboveInput')}</small>
      ) : !rateValid ? (
        <small className="field-error">{t('manualProviderModels.rateRequired')}</small>
      ) : null}
      <div className="form-actions">
        {rateState.kind === 'free' ? (
          <span className="badge active" title={t('manualProviderModels.freeControlsDisabled')}>
            {t('manualProviderModels.freeNotice')}
          </span>
        ) : null}
        <button
          type="button"
          disabled={
            busy ||
            !tenantId ||
            !probe?.ok ||
            !rateValid ||
            !targetTenants.length ||
            selected.status === 'disabled'
          }
          onClick={() => void deploy()}
        >
          {t('manualProviderModels.deploy')}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={
            busy ||
            !tenantId ||
            !rateValid ||
            !targetTenants.length ||
            activeDeployments.length === 0
          }
          onClick={() => void publishRates()}
        >
          {t('manualProviderModels.publishRate')}
        </button>
        <button type="button" className="secondary" onClick={onOpenAssignment}>
          {t('manualProviderModels.openAssignment')}
        </button>
      </div>
      <div className="credential-list">
        {activeDeployments.map((deployment) => (
          <div className="credential-row" key={deployment.tenantId}>
            <span className="credential-info">
              <strong>{deployment.tenantId}</strong>
              <small>{deployment.catalogModelId}</small>
              {deployment.currentRate ? (
                <small>
                  {t('manualProviderModels.currentRateSummary', {
                    input: deployment.currentRate.inputUsdPerMillion,
                    output: deployment.currentRate.outputUsdPerMillion,
                    cached: deployment.currentRate.cachedInputUsdPerMillion ?? deployment.currentRate.inputUsdPerMillion,
                    cacheWrite: deployment.currentRate.cacheWriteInputUsdPerMillion ?? deployment.currentRate.inputUsdPerMillion,
                    margin: deployment.currentRate.marginMultiplier,
                  })}
                </small>
              ) : null}
              {deployment.currentRate?.promptTiers?.map((tier) => (
                <small key={tier.minimumInputTokens}>
                  {t('manualProviderModels.promptTierSummary', {
                    minimum: tier.minimumInputTokens,
                    input: tier.inputUsdPerMillion,
                    output: tier.outputUsdPerMillion,
                  })}
                </small>
              ))}
              {deployment.rateVersion ? (
                <small>
                  {t('manualProviderModels.rateVersion')}: {deployment.rateVersion}
                </small>
              ) : (
                <span className="badge active">{t('manualProviderModels.freeBadge')}</span>
              )}
            </span>
            <button
              type="button"
              className="secondary danger"
              disabled={busy || !tenantId}
              onClick={() => void revoke(deployment.tenantId)}
            >
              {t('manualProviderModels.revoke')}
            </button>
          </div>
        ))}
      </div>
      <small>{t('manualProviderModels.historyRetained')}</small>
    </section>
  );
}
